import React, { createContext, useContext, useEffect, useState } from 'react';
import { IProviderProps } from '../@types/ContextProviderRelated';
import { IFilesApiProduct, TTexture } from '../@types/FilesAPIModels';
import useFilesApi from '../hooks/useFilesApi';

export interface IRoomComponentsDataContext {
  products: IFilesApiProduct[];
  models2D: any[];
  models3D: any[];
  attributes: any[];
  categories: any[];
  manufacturers: any[];
  loadProgress: number;
  loadCountTarget: number;
  apiFetchCompleted: boolean;
  isUserSearching: boolean;
  searchLoadProgress: number;
  searchLoadTarget: number;
  textureProducts: IFilesApiProduct[];
  textures: TTexture[];
  textureLoadProgress: number;
  textureLoadTarget: number;
  textureFetchCompleted: boolean;
  isFetchingProductsAndModels: boolean;
  isFetchingTextures: boolean;
  setApiFetchCompleted: (completed: boolean) => void; 
  setIsUserSearching: (isSearching: boolean) => void;
  setSearchLoadProgress: (progress: number) => void;
  setSearchLoadTarget: (target: number) => void;
  ensureAllProductsAndModels: () => Promise<void>;
  ensureTextures: () => Promise<void>;
  refetchAllProductsAndModels: () => Promise<void>;
  refetchTextures: () => Promise<void>;
}

export const RoomComponentsDataContext = createContext<IRoomComponentsDataContext | undefined>(undefined);

export const useRoomComponentsData = () => {
  const context = useContext(RoomComponentsDataContext);
  if (!context) {
    throw new Error('useRoomComponentsData must be used within a RoomComponentsDataProvider');
  }
  return context;
};

export const RoomComponentsDataProvider = ({ children }: IProviderProps): JSX.Element => {
  // Call useFilesApi directly within this provider
  const {
    products,
    textureProducts,
    manufacturers,
    attributes,
    categories,
    isLoading: isFilesApiLoading,
    loadProgress,
    loadTarget,
    textureLoadProgress,
    textureLoadTarget,
    textures,
    textureFetchCompleted,
    models3D,
    models2D,
    isFetchingProductsAndModels,
    isFetchingTextures,
    ensureAllProductsAndModels,
    ensureTextures,
    refetchAllProductsAndModels,
    refetchTextures,
    error, 
  } = useFilesApi();

  // Local state for search and derived API fetch status
  const [apiFetchCompleted, setApiFetchCompleted] = useState<boolean>(false); 
  const [isUserSearching, setIsUserSearching] = useState<boolean>(false);
  const [searchLoadProgress, setSearchLoadProgress] = useState<number>(0);
  const [searchLoadTarget, setSearchLoadTarget] = useState<number>(0);

  // Derive apiFetchCompleted from isFilesApiLoading
  useEffect(() => {
    setApiFetchCompleted(!isFilesApiLoading);
  }, [isFilesApiLoading]);

  const contextValue: IRoomComponentsDataContext = {
    products,
    models2D,
    models3D,
    attributes,
    categories,
    manufacturers,
    loadProgress,
    loadCountTarget: loadTarget,
    apiFetchCompleted,
    isUserSearching,
    searchLoadProgress,
    searchLoadTarget,
    textureProducts,
    textures,
    textureLoadProgress,
    textureLoadTarget,
    textureFetchCompleted,
    isFetchingProductsAndModels,
    isFetchingTextures,
    setApiFetchCompleted, 
    setIsUserSearching,
    setSearchLoadProgress,
    setSearchLoadTarget,
    ensureAllProductsAndModels,
    ensureTextures,
    refetchAllProductsAndModels, 
    refetchTextures, 
  };

  return (
    <RoomComponentsDataContext.Provider value={contextValue}>
      {children}
    </RoomComponentsDataContext.Provider>
  );
};

export default RoomComponentsDataProvider;
